import CheckIcon from "@mui/icons-material/Check";
import { Box, Button, Dialog, DialogActions, DialogContent, Typography } from "@mui/material";
import DialogTitle from "apps/web/src/componentLibrary/DialogTitle";
import { getMenuItemDialogTitle } from "apps/web/src/components/MenuItem/Dialog/getMenuItemDialogTitle";
import { MenuItemFullEditForm } from "apps/web/src/components/MenuItem/Form/Form";
import { EditMenuItemHeader } from "apps/web/src/components/MenuItem/Header/EditMenuItemHeader";
import { newRecipeFormState, newRecipeFormStateFromGPTRecipe, recipeFormReducer } from "apps/web/src/components/Recipe/Form/utils";
import { GenerateWithGPTDialog } from "apps/web/src/components/Recipe/GPT/Generator/GenerateWithGPTDialog";
import { openTrackedRecipeAiFlow } from "apps/web/src/components/Recipe/GPT/recipeAiFlow";
import { TranscribeWithGPTDialog } from "apps/web/src/components/Recipe/GPT/Transcriber/TranscribeWithGPTDialog";
import { TranscribeError } from "apps/web/src/components/Recipe/GPT/Transcriber/utils";
import RecipeCreateDialog from "apps/web/src/components/Recipe/RecipeCreateDialog";
import { useSnackbar } from "apps/web/src/components/Snackbar/SnackbarContext";
import DiscardChangesDialog from "apps/web/src/components/universal/DiscardChangesDialog";
import { useEnvironmentType } from "apps/web/src/contexts/useEnvType";
import { useNutritionFlags } from "apps/web/src/hooks/useNutritionFlags";
import { useRevisionGatedAiFlow, type RecipeAiFlow } from "apps/web/src/hooks/useRevisionGatedAiFlow";
import { GptRecipeFragment, MenuItemFormFragment, RecipeFullFragment } from "apps/web/src/types";
import { useCallback, useReducer, useState } from "react";
import { newServingAmount } from "shared-ui/ServingAmount/utils";
import { EditMenuItemState, editMenuItemReducer, toEditMenuItemState } from "./reducer";

interface MenuItemEditDialogContentProps {
  open: boolean;
  onClose: () => void;
  onSave: (menuItem: EditMenuItemState) => void;
  menuItem: MenuItemFormFragment;
  onDuplicate: () => void;
  pageView?: boolean;
}

export const MenuItemEditDialogContent = ({
  open,
  onClose,
  onSave,
  menuItem: initMenuItem,
  onDuplicate,
  pageView = false,
}: MenuItemEditDialogContentProps) => {
  const [discardChangesBeforeCloseOpen, setDiscardChangesBeforeCloseOpen] = useState(false);
  const [discardChangesBeforeDuplicateOpen, setDiscardChangesBeforeDuplicateOpen] = useState(false);
  const { setMessage } = useSnackbar();
  const { isCommercial } = useEnvironmentType();
  const { isRecipeAndMenuItemRedesignEnabled, isGetRecipeFromUrlEnabled, useBedrock } = useNutritionFlags();

  const [state, dispatch] = useReducer(editMenuItemReducer, toEditMenuItemState(initMenuItem));
  const [initialRecipeState, initialRecipeDispatch] = useReducer(
    recipeFormReducer,
    undefined || newRecipeFormState(initMenuItem.name, initMenuItem.servingAmounts)
  );
  const [showGenerateRecipeModal, setShowGenerateRecipeModal] = useState(false);
  const [showTranscribePhotoModal, setShowTranscribePhotoModal] = useState(false);
  const [showImportLinkModal, setShowImportLinkModal] = useState(false);
  const [transcriberError, setTranscriberError] = useState<TranscribeError | null>(null);
  const [showCreateRecipeModal, setShowCreateRecipeModal] = useState(false);

  const showAiRecipes = isCommercial || useBedrock;
  // Redesign is Kitchen → Menu Items create/edit pages only (not nested dialogs).
  const useMenuItemRedesign = pageView && isRecipeAndMenuItemRedesignEnabled;
  const dialogTitle = getMenuItemDialogTitle("Edit Reusable Menu Item", useMenuItemRedesign);

  const openAiFlow = useCallback((flow: RecipeAiFlow) => {
    openTrackedRecipeAiFlow(flow, "Menu item", {
      generate: () => setShowGenerateRecipeModal(true),
      photo: () => setShowTranscribePhotoModal(true),
      link: () => setShowImportLinkModal(true),
    });
  }, []);

  const makeRevision = useCallback(() => {
    dispatch({ type: "MenuItemMakeRevision" });
  }, []);

  const { isAwaitingRevisionForAi, requestAiFlow, cancelPendingAiFlow, confirmRevisionAndOpenAi } = useRevisionGatedAiFlow({
    isRevision: state.isRevision,
    onMakeRevision: makeRevision,
    onOpenFlow: openAiFlow,
  });

  const showRevisionDialog = state.pendingRevisionAction !== null || isAwaitingRevisionForAi;

  const handleRevisionClose = () => {
    if (isAwaitingRevisionForAi) {
      cancelPendingAiFlow();
      return;
    }
    dispatch({ type: "MenuItemCancelRevision" });
  };

  const handleClose = () => {
    if (state.edited) {
      setDiscardChangesBeforeCloseOpen(true);
    } else {
      onClose();
    }
  };

  const handleDuplicate = () => {
    if (state.edited) {
      setDiscardChangesBeforeDuplicateOpen(true);
    } else if (pageView) {
      onDuplicate();
    } else {
      onClose();
      onDuplicate();
    }
  };

  const onGenerateError = () => {
    setMessage("error", "Error with generating a recipe");
  };

  const onGPTRecipeComplete = (gptRecipe: GptRecipeFragment, closeModal: () => void) => {
    const newState = newRecipeFormStateFromGPTRecipe(gptRecipe);
    initialRecipeDispatch({
      type: "REPLACE_STATE",
      payload: newState,
    });
    closeModal();
    setShowTranscribePhotoModal(false);
    setShowImportLinkModal(false);
    setShowCreateRecipeModal(true);
  };

  const onSaveAiRecipe = (recipe: RecipeFullFragment) => {
    setMessage("success", `${recipe.name} has been created.`);
    const newRecipeServing = recipe.servings[0];
    const newRecipeServingAmounts = [
      newServingAmount(
        {
          ...newRecipeServing,
          foodOrRecipe: recipe,
        },
        1
      ),
    ];
    dispatch({
      type: "MenuItemChangeServingAmountsAction",
      payload: {
        servingAmounts: newRecipeServingAmounts,
      },
    });
  };

  const form = (
    <MenuItemFullEditForm
      variant="MenuItem"
      state={state}
      dispatch={dispatch}
      onAiGenerateClicked={showAiRecipes ? () => requestAiFlow("generate") : null}
      onAiTranscribeClicked={showAiRecipes ? () => requestAiFlow("photo") : null}
      onImportFromLinkClicked={showAiRecipes && isGetRecipeFromUrlEnabled ? () => requestAiFlow("link") : undefined}
      enableRedesign={useMenuItemRedesign}
    />
  );

  const nestedDialogs = (
    <>
      {showRevisionDialog && (
        <Dialog open={showRevisionDialog} onClose={handleRevisionClose} maxWidth="sm" fullWidth>
          <DialogTitle title="Create Revision of Menu Item?" onClose={handleRevisionClose} />
          <DialogContent>
            <Typography>Any changes to a menu item that affects its nutrients will create a new revision.</Typography>
            <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
              <CheckIcon sx={{ pr: 1 }} />
              <Typography>
                Usage on past menus and orders will preserve existing nutrients.
                <br />
                Usage on future menus and orders will be updated to the new revision.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={handleRevisionClose}>
              Cancel
            </Button>
            <Button onClick={confirmRevisionAndOpenAi}>Create Revision</Button>
          </DialogActions>
        </Dialog>
      )}

      {discardChangesBeforeCloseOpen && (
        <DiscardChangesDialog
          open={discardChangesBeforeCloseOpen}
          onClose={() => setDiscardChangesBeforeCloseOpen(false)}
          onDiscard={onClose}
        />
      )}
      {discardChangesBeforeDuplicateOpen && (
        <DiscardChangesDialog
          open={discardChangesBeforeDuplicateOpen}
          onClose={() => setDiscardChangesBeforeDuplicateOpen(false)}
          onDiscard={() => {
            onDuplicate();
            if (!pageView) {
              onClose();
            }
          }}
        />
      )}
      {showGenerateRecipeModal && (
        <GenerateWithGPTDialog
          open={showGenerateRecipeModal}
          onClose={() => setShowGenerateRecipeModal(false)}
          onGenerateComplete={onGPTRecipeComplete}
          onError={onGenerateError}
          initialName={state.menuItem.name}
        />
      )}
      {showTranscribePhotoModal && (
        <TranscribeWithGPTDialog
          open={showTranscribePhotoModal}
          onClose={() => setShowTranscribePhotoModal(false)}
          onTranscribeComplete={onGPTRecipeComplete}
          transcriberError={transcriberError}
          setTranscriberError={setTranscriberError}
          onError={onGenerateError}
          mode="photo"
        />
      )}
      {showImportLinkModal && (
        <TranscribeWithGPTDialog
          open={showImportLinkModal}
          onClose={() => setShowImportLinkModal(false)}
          onTranscribeComplete={onGPTRecipeComplete}
          transcriberError={transcriberError}
          setTranscriberError={setTranscriberError}
          onError={onGenerateError}
          mode="link"
        />
      )}
      {showCreateRecipeModal && (
        <RecipeCreateDialog
          open={showCreateRecipeModal}
          onClose={() => setShowCreateRecipeModal(false)}
          onCreate={recipe => onSaveAiRecipe(recipe)}
          initialRecipeState={initialRecipeState}
        />
      )}
    </>
  );

  if (useMenuItemRedesign) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <EditMenuItemHeader title={dialogTitle} handleClose={handleClose} handleSave={() => onSave(state)} handleDuplicate={handleDuplicate} />
        {form}
        {nestedDialogs}
      </Box>
    );
  }

  return (
    <Dialog
      PaperProps={{
        sx: { minHeight: "calc(100% - 20px)" },
      }}
      open={open}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
    >
      <DialogTitle title={dialogTitle} onClose={handleClose} />
      <DialogContent>{form}</DialogContent>
      <DialogActions sx={{ justifyContent: "space-between" }}>
        <Button sx={{ alignSelf: "start", pl: 2 }} variant="outlined" onClick={handleDuplicate}>
          Duplicate Menu Item
        </Button>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="outlined" onClick={() => handleClose()}>
            Close
          </Button>
          <Button onClick={() => onSave(state)}>Save</Button>
        </Box>
      </DialogActions>
      {nestedDialogs}
    </Dialog>
  );
};
